/**
 * ConcentrationPill — single render of concentration state across web panels.
 *
 * Display contract (matches mobile + UX invariant in
 * docs/combat/CONCENTRATION.md):
 *   - isConcentrating=false → renders nothing
 *   - isConcentrating=true, spellName set → "🌀 CONCENTRATING: <SPELL>"
 *   - isConcentrating=true, no spellName → "🌀 CONCENTRATING"
 *
 * The friendly gate (whether the viewer sees the spell name) is enforced
 * server-side via `ObservableCharacterView.concentrationSpell` and per-
 * recipient masking on the `ConcentrationStarted` WS event. This component
 * doesn't make visibility decisions — it just renders the prop.
 *
 * Canonical glyph 🌀 (matches mobile). Drop the legacy ✨ Sparkles icon.
 */

import { cn } from '../../lib/utils';

interface ConcentrationPillProps {
  isConcentrating: boolean;
  spellName?: string | null;
  className?: string;
}

export function ConcentrationPill({
  isConcentrating,
  spellName,
  className,
}: ConcentrationPillProps) {
  if (!isConcentrating) return null;

  const label = spellName ? `CONCENTRATING: ${spellName.toUpperCase()}` : 'CONCENTRATING';

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 overflow-hidden rounded px-1.5 py-0.5 text-xs font-semibold whitespace-nowrap',
        'bg-purple-500/20 text-purple-200',
        className
      )}
      aria-label={spellName ? `Concentrating on ${spellName}` : 'Concentrating'}
      title={label}
    >
      <span aria-hidden="true" className="shrink-0">
        🌀
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
}
